import { profile } from 'lang/en.json';
import styles from 'styles/modules/ProfileSidebar.module.scss';
import AddContent from './AddContent';
import ProfileSidebarAddress from './ProfileSidebarAddress';
import { Fragment, useEffect, useRef, useState } from 'react';
import EditContent from './EditContent';
import { useDispatch, useSelector } from 'react-redux';
import { handleShowSidescreen } from 'store/ux';
import {
    getFileExtension,
    getFileExtPreview,
    getImageTypes,
} from 'helpers/file';
import Typo from 'components/Typo';
import { authSelector } from 'store/auth';

const ProfileSidebar = ({ data }) => {
    const dispatch = useDispatch();
    const companyData = useSelector((state) => state.company.data);
    const [filesToShow, setFilesToShow] = useState(6);
    const filesRef = useRef(null);
    const auth = useSelector(authSelector);

    const [logo, setLogo] = useState(data.logo);
    const [files, setFiles] = useState(data.files);

    useEffect(() => {
        setLogo(companyData.logo);
        setFiles(companyData.files);
    }, [companyData.logo, companyData.files]);

    const handleAddFile = () => {
        dispatch(handleShowSidescreen('add-file'));
    };

    const handleAddAddress = () => {
        dispatch(handleShowSidescreen('add-address'));
    };

    const handleEditAddress = () => {
        dispatch(handleShowSidescreen('edit-addresses'));
    };

    const handleEditInfo = () => {
        dispatch(handleShowSidescreen('edit-info'));
    };

    const handleEditLogo = () => {
        dispatch(handleShowSidescreen('edit-info'));
    };

    let logoSection = null;

    if (logo) {
        logoSection = (
            <>
                <img src={logo.path} alt={`${data.name} Logo`} />
                <div className={styles.editLogoButton}>
                    <EditContent
                        data={{
                            label: 'Edit logo',
                            onClick: handleEditLogo,
                        }}
                    />
                </div>
            </>
        );
    } else {
        logoSection = (
            <AddContent
                data={{
                    direction: 'column',
                    label: auth.isJournalist
                        ? 'Add your photo'
                        : 'Add your logo or a picture',
                    onClick: handleEditInfo,
                }}
            />
        );
    }
    const handleWindowSize = () => {
        const maxItems =
            window.innerWidth < 375 ? 2 : window.innerWidth < 768 ? 3 : 6;
        setFilesToShow(maxItems);
    };

    let loaderTimeout;

    useEffect(() => {
        loaderTimeout = setTimeout(handleWindowSize, 410);
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleWindowSize);
        }
        return () => clearTimeout(loaderTimeout);
    });

    let filesSection;

    // todo: move to a separate file
    if (!files || files.length < 1) {
        let items = [];
        for (let i = 0; i < filesToShow - 1; i++) {
            items.push(
                <div
                    className={[
                        'border-gray',
                        styles.filesItem,
                        styles.filesPlaceholder,
                    ].join(' ')}
                />
            );
        }

        filesSection = (
            <>
                <div className={['border-gray', styles.filesItem].join(' ')}>
                    <AddContent
                        data={{
                            direction: 'column',
                            label: 'Add file',
                            onClick: handleAddFile,
                        }}
                    />
                </div>

                {items.map((item, key) => {
                    return <Fragment key={`file-item-${key}`}>{item}</Fragment>;
                })}
            </>
        );
    } else {
        let adder = null;
        let items = [];

        const moreFiles = files.length >= filesToShow;
        const maxItems = moreFiles ? filesToShow : filesToShow - 1;

        for (let i = 0; i < maxItems; i++) {
            if (files[i]) {
                let overlay = null;
                if (i === filesToShow - 1 && filesToShow !== files.length) {
                    overlay = (
                        <div className={styles.filesOverlay}>{`+${
                            files.length - filesToShow
                        }`}</div>
                    );
                }

                const imageTypes = getImageTypes();
                const thisExt = getFileExtension(files[i].name);
                const isImage = imageTypes.includes(thisExt);
                let fileItem = null;

                if (isImage) {
                    fileItem = (
                        <div
                            className={[
                                // 'border-gray',
                                styles.filesItem,
                                styles.filesBackground,
                            ].join(' ')}
                            style={{ background: `url(${files[i].path})` }}
                            onClick={handleAddFile}
                        >
                            {overlay}
                        </div>
                    );
                } else {
                    fileItem = (
                        <div
                            className={['border-gray', styles.filesItem].join(
                                ' '
                            )}
                            onClick={handleAddFile}
                        >
                            <div className={styles.fileExtPreview}>
                                {getFileExtPreview(thisExt)}
                            </div>

                            {overlay}
                        </div>
                    );
                }
                items.push(fileItem);
            } else {
                items.push(
                    <div
                        className={[
                            'border-gray',
                            styles.filesItem,
                            styles.filesPlaceholder,
                        ].join(' ')}
                    />
                );
            }
        }

        if (!moreFiles) {
            adder = (
                <div className={['border-gray', styles.filesItem].join(' ')}>
                    <AddContent
                        data={{
                            direction: 'column',
                            label: 'Add file',
                            onClick: handleAddFile,
                        }}
                    />
                </div>
            );
        }

        filesSection = (
            <>
                {items.map((item, key) => (
                    <Fragment key={`file-item-${key}`}>{item}</Fragment>
                ))}
                {adder}
            </>
        );
    }

    return (
        <div className={styles.sidebar}>
            {/* LOGO */}
            <section
                className={['border-gray', styles.block, styles.logo].join(' ')}
            >
                {logoSection}
            </section>

            {/* ADDRESS */}
            <section
                className={['border-gray', styles.block, styles.address].join(
                    ' '
                )}
            >
                <ProfileSidebarAddress
                    handleAddAddress={handleAddAddress}
                    handleEditAddress={handleEditAddress}
                />
            </section>
            {data.isJournalist === false && (
                <section className={styles.files} ref={filesRef}>
                    <header>
                        <Typo
                            Tag={'h3'}
                            variant={'eyebrow'}
                            className={styles.filesTitle}
                        >
                            <span data-nosnippet>
                                {profile.mediaPack.title}
                            </span>
                        </Typo>
                    </header>
                    <div className={styles.filesWrapper}>{filesSection}</div>
                </section>
            )}
        </div>
    );
};

export default ProfileSidebar;
